import * as React from "react";
import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { confirmationMatches } from "@/lib/removal";

/** The warning before a removal the Privacy Policy describes (an account,
 *  an organisation): what is deleted, what is kept, and a field where the
 *  person types ``expected`` (an email address or an organisation name)
 *  before the action button turns on. */
export function TypedRemovalDialog(props: {
  open: boolean;
  onClose: () => void;
  title: string;
  intro: string;
  deleted: string[];
  kept: string[];
  confirmLabel: string;
  expected: string;
  actionLabel: string;
  busy: boolean;
  error: string | null;
  /** A way out of the error, shown on it (e.g. "Cancel the plan first"). */
  errorAction?: React.ReactNode;
  onConfirm: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [typed, setTyped] = useState("");
  // Start empty each time the dialog opens (adjusting state while
  // rendering, not in an effect).
  const [wasOpen, setWasOpen] = useState(props.open);
  if (props.open !== wasOpen) {
    setWasOpen(props.open);
    if (props.open) setTyped("");
  }
  const ok = confirmationMatches(typed, props.expected);
  return (
    <Dialog open={props.open} onClose={props.onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{props.title}</DialogTitle>
      <DialogContent>
        <Stack sx={{ gap: 2, mt: 1 }}>
          <DialogContentText>
            {t("removal.cannot_undo")} {props.intro}
          </DialogContentText>
          <Section heading={t("removal.deleted_heading")} items={props.deleted} />
          <Section heading={t("removal.kept_heading")} items={props.kept} />
          {props.error && (
            <Alert severity="error" sx={{ whiteSpace: "pre-line" }} action={props.errorAction}>
              {props.error}
            </Alert>
          )}
          <TextField
            label={props.confirmLabel}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            size="small"
            fullWidth
            autoComplete="off"
            slotProps={{ htmlInput: { "data-testid": "removal-confirm-input" } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>{t("common.cancel")}</Button>
        <Button
          variant="contained"
          color="error"
          disabled={!ok || props.busy}
          onClick={props.onConfirm}
        >
          {props.actionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Section({ heading, items }: { heading: string; items: string[] }): React.JSX.Element {
  return (
    <Box>
      <Typography variant="subtitle2">{heading}</Typography>
      <Box component="ul" sx={{ m: 0, pl: 3 }}>
        {items.map((item) => (
          <Typography key={item} component="li" variant="body2">
            {item}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}
